import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  createAgeRestrictionSetting,
  updateAgeRestrictionSetting,
  deleteAgeRestrictionSetting,
} from '../api/ageRestrictionSettings';
import { toastError } from '../../../shared/toastHelper';
import useMatrixDraft from '../hooks/useMatrixDraft';
import MatrixToolbar from './MatrixToolbar';
import MatrixGrid from './MatrixGrid';
import MatrixFooter from './MatrixFooter';
import '../styles/ageRestrictionsSettings.css';

const AgeRestrictionsMatrix = ({
  teamId,
  csrfToken,
  initialSettings,
  venueOwnerships,
  initialVenueAgeRestrictions,
}) => {
  const [settings, setSettings] = useState(initialSettings || []);
  const [newName, setNewName] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const { draft, isDirty, saving, cycleCell, saveAll, cancel, pruneSetting } = useMatrixDraft({
    teamId,
    csrfToken,
    venueOwnerships,
    initialVenueAgeRestrictions,
  });

  const venuesWithDefault = useMemo(
    () => venueOwnerships.filter((vo) => draft[vo.id]?.defaultId).length,
    [venueOwnerships, draft]
  );

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      const created = await createAgeRestrictionSetting(teamId, trimmed, csrfToken);
      setSettings((prev) => [...prev, created]);
      setNewName('');
    } catch (err) {
      toastError(
        err.response?.data?.errors?.[0] || 'Could not add age restriction option'
      );
    }
  };

  const handleRename = async (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const current = settings.find((s) => s.id === id);
    if (current && current.name === trimmed) return;
    try {
      const updated = await updateAgeRestrictionSetting(
        teamId,
        id,
        { name: trimmed },
        csrfToken
      );
      setSettings((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } catch (err) {
      toastError(
        err.response?.data?.errors?.[0] || 'Could not rename age restriction option'
      );
    }
  };

  const handleRemove = async (id) => {
    try {
      await deleteAgeRestrictionSetting(teamId, id, csrfToken);
      setSettings((prev) => prev.filter((s) => s.id !== id));
      pruneSetting(id);
    } catch (err) {
      toastError(
        err.response?.data?.errors?.[0] || 'Could not remove age restriction option'
      );
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = settings.findIndex((s) => s.id === active.id);
    const newIndex = settings.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = settings;
    const reordered = arrayMove(settings, oldIndex, newIndex);
    setSettings(reordered);
    try {
      await updateAgeRestrictionSetting(teamId, active.id, { position: newIndex + 1 }, csrfToken);
    } catch (err) {
      setSettings(previous);
      toastError(err.response?.data?.errors?.[0] || 'Could not save new order');
    }
  };

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3 className="ar-card__title">Age restrictions</h3>
        <p className="ar-card__subtitle">
          One row per option, one column per venue. Click a cell to enable; click again to set as the venue default.
        </p>
      </div>

      <MatrixToolbar
        newName={newName}
        setNewName={setNewName}
        onAdd={handleAdd}
      />

      <MatrixGrid
        settings={settings}
        venueOwnerships={venueOwnerships}
        draft={draft}
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onCycleCell={cycleCell}
        onRename={handleRename}
        confirmRemoveId={confirmRemoveId}
        setConfirmRemoveId={setConfirmRemoveId}
        onRemoveConfirmed={handleRemove}
      />

      <MatrixFooter
        isDirty={isDirty}
        saving={saving}
        optionCount={settings.length}
        venueCount={venueOwnerships.length}
        venuesWithDefault={venuesWithDefault}
        onCancel={cancel}
        onSave={saveAll}
      />
    </div>
  );
};

AgeRestrictionsMatrix.propTypes = {
  teamId: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
  initialSettings: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired })
  ).isRequired,
  venueOwnerships: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired })
  ).isRequired,
  initialVenueAgeRestrictions: PropTypes.object.isRequired,
};

export default AgeRestrictionsMatrix;
